import { Field } from 'formik';

import CustomLink from '@perpay-web/fintech/components/base/CustomLink/CustomLink';
import SchumerBox from '@perpay-web/fintech/components/base/SchumerBox/SchumerBox';
import {
    AUTOMATIC_TRANSFERS_AGREEMENT_URL,
    CREDIT_CARDHOLDER_AGREEMENT_URL,
    DIRECT_DEPOSIT_TERMS_URL,
    ELECTRONIC_CONSENT_POLICY_URL,
    PRIVACY_NOTICE_URL,
    PRIVACY_POLICY_URL,
    REWARDS_PROGRAM_URL,
    USA_PATRIOT_ACT_NOTICE_URL,
} from '@perpay-web/fintech/constants/urls';
import { getClassName } from '@perpay-web/utils/domUtils';

import styles from '@perpay-web/fintech/components/composite/CardApplicationEmploymentDetails/CardApplicationEmploymentDetails.scss';

const CardApplicationTermsAndConditions = ({
    formik,
    isPhoneVerified = false,
    onClickTransfersAgreement,
    onToggleAutoApply,
    onToggleCardHolderAgreement,
    onToggleCreditReportingAgreement,
    onToggleReceiveText,
    onClickAgreement,
    onClickConsent,
    onClickDirectDeposit,
    onClickPatriot,
    onClickPrivacyNotice,
    onClickPrivacyPolicy,
    onClickRewards,
}) => {
    const handleIndividualCheckboxChange = (checkboxName, checked) => {
        formik.setFieldValue(checkboxName, checked);

        // Determine if all checkboxes are checked
        const allChecked =
            (checkboxName === 'optedInToAutoApply'
                ? checked
                : formik.values.optedInToAutoApply) &&
            (checkboxName === 'acceptedFairCreditReportingAgreement'
                ? checked
                : formik.values.acceptedFairCreditReportingAgreement) &&
            (checkboxName === 'acceptedCardHolderAgreement'
                ? checked
                : formik.values.acceptedCardHolderAgreement) &&
            (isPhoneVerified ||
                (checkboxName === 'receiveText'
                    ? checked
                    : formik.values.receiveText));

        formik.setFieldValue('acceptAll', allChecked);
    };

    return (
        <>
            <div className={styles.subtitle__terms}>Terms and Conditions</div>
            <label
                className={styles.label}
                htmlFor='optedInToAutoApply'
                aria-label='accept automatic transfers agreement'
            >
                <Field
                    id='optedInToAutoApply'
                    as='input'
                    className={styles.checkbox}
                    type='checkbox'
                    name='optedInToAutoApply'
                    checked={formik.values.optedInToAutoApply || false}
                    onChange={(event) => {
                        handleIndividualCheckboxChange(
                            'optedInToAutoApply',
                            event.target.checked,
                        );
                        onToggleAutoApply({
                            optedInToAutoApply: event.target.checked,
                            autoTransferModal: false,
                        });
                    }}
                />
                <div className={styles['checkbox-image']}>
                    <div className={styles['checkbox-icon']} />
                </div>
                <div className={getClassName(styles['checkbox-text'])}>
                    <div className={styles['checkbox-text-section']}>
                        By checking this box, I agree to the{' '}
                        <CustomLink
                            onClick={onClickTransfersAgreement}
                            href={AUTOMATIC_TRANSFERS_AGREEMENT_URL}
                            target='_blank'
                        >
                            Perpay Automatic Transfers Agreement
                        </CustomLink>
                        , which authorizes Perpay to automatically transfer
                        funds to pay my credit card balance every day.{' '}
                        <span className={styles['checkbox-text-important']}>
                            I understand that enrolling in automatic transfers
                            is voluntary and is not a condition for credit.
                        </span>
                    </div>
                </div>
            </label>
            <label
                className={styles.label}
                htmlFor='acceptedFairCreditReportingAgreement'
                aria-label='accept credit agreements'
            >
                <Field
                    id='acceptedFairCreditReportingAgreement'
                    as='input'
                    className={styles.checkbox}
                    type='checkbox'
                    name='acceptedFairCreditReportingAgreement'
                    checked={
                        formik.values.acceptedFairCreditReportingAgreement ||
                        false
                    }
                    onChange={(event) => {
                        handleIndividualCheckboxChange(
                            'acceptedFairCreditReportingAgreement',
                            event.target.checked,
                        );
                        onToggleCreditReportingAgreement(event.target.checked);
                    }}
                />
                <div className={styles['checkbox-image']}>
                    <div className={styles['checkbox-icon']} />
                </div>
                <div className={styles['checkbox-text']}>
                    <div className={styles['checkbox-text-section-padding']}>
                        I understand that by applying for the Perpay Credit Card
                        and clicking this box, I am providing &quot;written
                        instructions&quot; to Perpay under the Fair Credit
                        Reporting Act (FCRA) authorizing Perpay to obtain my
                        information from one or more consumer reporting
                        agencies.
                    </div>
                    <div className={styles['checkbox-text-section-padding']}>
                        I authorize Perpay to verify information in my credit
                        application, and I agree that Perpay may contact third
                        parties to verify any such information. Perpay may use
                        my consumer report(s) to authenticate my identity, to
                        make credit decisions, and for any related purposes. I
                        authorize Perpay to obtain consumer reports at various
                        times in connection with credit line reviews and
                        adjustments (increases or decreases) and the servicing,
                        monitoring, or collection of this credit card.
                    </div>
                    <div className={styles['checkbox-text-section']}>
                        I further acknowledge that Perpay may report credit data
                        from my Perpay card to credit reporting agencies and
                        that the Perpay Credit Card is issued by Celtic Bank.
                    </div>
                </div>
            </label>
            <label
                className={styles.label}
                htmlFor='acceptedCardHolderAgreement'
                aria-label='read and accept agreements'
            >
                <Field
                    id='acceptedCardHolderAgreement'
                    as='input'
                    className={styles.checkbox}
                    type='checkbox'
                    name='acceptedCardHolderAgreement'
                    checked={formik.values.acceptedCardHolderAgreement || false}
                    onChange={(event) => {
                        handleIndividualCheckboxChange(
                            'acceptedCardHolderAgreement',
                            event.target.checked,
                        );
                        onToggleCardHolderAgreement(event.target.checked);
                    }}
                />
                <div className={styles['checkbox-image']}>
                    <div className={styles['checkbox-icon']} />
                </div>
                <div className={styles['checkbox-text']}>
                    I have read, understand and agree to the{' '}
                    <CustomLink
                        onClick={onClickAgreement}
                        href={CREDIT_CARDHOLDER_AGREEMENT_URL}
                        target='_blank'
                    >
                        Credit Card Holder Agreement
                    </CustomLink>
                    ,{' '}
                    <CustomLink
                        onClick={onClickRewards}
                        href={REWARDS_PROGRAM_URL}
                        target='_blank'
                    >
                        Rewards Program
                    </CustomLink>
                    ,{' '}
                    <CustomLink
                        onClick={onClickDirectDeposit}
                        href={DIRECT_DEPOSIT_TERMS_URL}
                        target='_blank'
                    >
                        Payroll Direct Deposit Terms and Conditions
                    </CustomLink>
                    ,{' '}
                    <CustomLink
                        onClick={onClickConsent}
                        href={ELECTRONIC_CONSENT_POLICY_URL}
                        target='_blank'
                    >
                        Electronic Consent Policy
                    </CustomLink>
                    ,{' '}
                    <CustomLink
                        onClick={onClickPrivacyPolicy}
                        href={PRIVACY_POLICY_URL}
                        target='_blank'
                    >
                        Privacy Policy
                    </CustomLink>
                    ,{' '}
                    <CustomLink
                        onClick={onClickPrivacyNotice}
                        href={PRIVACY_NOTICE_URL}
                        target='_blank'
                    >
                        Privacy Notice
                    </CustomLink>{' '}
                    and the{' '}
                    <CustomLink
                        onClick={onClickPatriot}
                        href={USA_PATRIOT_ACT_NOTICE_URL}
                        target='_blank'
                    >
                        USA PATRIOT Act Notice
                    </CustomLink>
                    .
                </div>
            </label>
            {!isPhoneVerified ? (
                <label
                    className={styles.label}
                    htmlFor='receiveText'
                    aria-label='receive text'
                >
                    <Field
                        id='receiveText'
                        as='input'
                        className={styles.checkbox}
                        type='checkbox'
                        name='receiveText'
                        checked={formik.values.receiveText || false}
                        onChange={(event) => {
                            handleIndividualCheckboxChange(
                                'receiveText',
                                event.target.checked,
                            );
                            onToggleReceiveText(event.target.checked);
                        }}
                    />
                    <div className={styles['checkbox-image']}>
                        <div className={styles['checkbox-icon']} />
                    </div>
                    <div className={styles['checkbox-text']}>
                        <div className={styles['checkbox-text-section']}>
                            Keep me up to date with exclusive offers via text
                            message
                        </div>
                    </div>
                </label>
            ) : null}
            <SchumerBox className={styles.override__container} />
        </>
    );
};

export default CardApplicationTermsAndConditions;
